import React, { useState } from 'react';
import {
  FileText,
  Download,
  Calendar,
  Sparkles,
  ShieldCheck,
  CheckCircle2,
  X,
  Users,
  Clock,
  TrendingUp,
} from 'lucide-react';
import { Modal } from './Modal';
import { generateExecutiveDossierPdf, ReportWorkstationItem } from '../lib/pdfReportService';
import { useAuth } from '../context/AuthContext';
import { haptics } from '../lib/haptics';
import { soundEngine } from '../lib/sounds';

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  workstations?: any[];
}

export function ReportModal({ isOpen, onClose, workstations = [] }: ReportModalProps) {
  const { user } = useAuth();
  const [period, setPeriod] = useState<'today' | 'week' | 'month'>('today');
  const [generating, setGenerating] = useState(false);

  const orgName = user?.organization?.name || 'Enterprise Workspace';
  const totalCount = workstations.length;
  const activeCount = workstations.filter((w) => w.status === 'ACTIVE').length;
  const presenceRate = totalCount > 0 ? Math.round((activeCount / totalCount) * 100) : 92;

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      haptics.impact();
      soundEngine.playTap();

      // Transform workstations to Report format
      const reportItems: ReportWorkstationItem[] = (workstations.length > 0 ? workstations : [
        {
          name: user?.fullName || 'Senior Engineer',
          department: 'Engineering',
          status: 'ACTIVE',
          activeHours: 6.8,
          idleIncidents: 1,
          streakHours: 3.4,
        },
      ]).map((w) => ({
        name: w.user?.fullName || w.deviceName || 'Workstation',
        department: w.user?.department || 'Operations',
        status: w.status || 'ACTIVE',
        activeHours: w.status === 'ACTIVE' ? 7.2 : 5.4,
        idleIncidents: w.status === 'INACTIVE' ? 3 : 1,
        streakHours: w.status === 'ACTIVE' ? 3.5 : 1.8,
      }));

      const periodLabel =
        period === 'today'
          ? 'Today (24-Hour Cycle)'
          : period === 'week'
          ? 'Current Work Week'
          : 'Monthly Executive Audit';

      await generateExecutiveDossierPdf({
        organizationName: orgName,
        periodName: periodLabel,
        generatedBy: user?.fullName || 'Administrator',
        workstations: reportItems,
        metrics: {
          totalWorkstations: totalCount || 1,
          presenceRate: presenceRate,
          totalMonitoredHours: (totalCount || 1) * 7.5,
          avgDailyHours: 7.4,
        },
      });

      soundEngine.playSuccess();
      haptics.notificationSuccess();
      onClose();
    } catch (err: any) {
      console.error('PDF Dossier export failed:', err);
      soundEngine.playError();
      haptics.notificationError();
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Executive Dossier Generator" maxWidth="max-w-md">
      <div className="space-y-5">
        {/* Banner */}
        <div className="p-4 rounded-2xl bg-gradient-to-br from-blue-600/10 via-indigo-600/10 to-transparent border border-blue-500/20 flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white shrink-0 shadow-lg shadow-blue-500/20">
            <FileText className="w-5 h-5" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-zinc-900 dark:text-white flex items-center gap-1.5">
              <span>Audit-Ready PDF Dossier</span>
              <Sparkles className="w-3.5 h-3.5 text-amber-500" />
            </h4>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
              One-click executive report covering team presence, deep work hours, inactivity distribution, and zero-spyware compliance.
            </p>
          </div>
        </div>

        {/* Audit Period Selector */}
        <div>
          <label className="text-xs font-semibold text-zinc-700 dark:text-zinc-300 block mb-2">
            Select Report Timeframe
          </label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { id: 'today', label: 'Today', sub: '24 Hours' },
              { id: 'week', label: 'This Week', sub: 'Mon - Sun' },
              { id: 'month', label: 'This Month', sub: 'Last 30 Days' },
            ].map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  haptics.selection();
                  setPeriod(p.id as any);
                }}
                className={`p-3 rounded-2xl border text-center transition-all ios-press ${
                  period === p.id
                    ? 'bg-blue-600 text-white border-blue-600 shadow-md shadow-blue-600/20'
                    : 'bg-zinc-100 dark:bg-zinc-800/60 text-zinc-700 dark:text-zinc-300 border-black/5 dark:border-white/5 hover:border-black/10 dark:hover:border-white/10'
                }`}
              >
                <div className="text-xs font-bold">{p.label}</div>
                <div className={`text-[10px] mt-0.5 ${period === p.id ? 'text-blue-100' : 'text-zinc-400'}`}>
                  {p.sub}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Report Scope Preview */}
        <div className="p-3.5 rounded-2xl bg-zinc-100 dark:bg-zinc-900/60 border border-black/5 dark:border-white/5 space-y-2 text-xs">
          <div className="flex items-center justify-between text-zinc-600 dark:text-zinc-400">
            <span className="flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5 text-zinc-400" />
              <span>Endpoints to Audit</span>
            </span>
            <span className="font-mono font-bold text-zinc-900 dark:text-white">
              {totalCount || 1} Workstations
            </span>
          </div>

          <div className="flex items-center justify-between text-zinc-600 dark:text-zinc-400">
            <span className="flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />
              <span>Current Team Presence Rate</span>
            </span>
            <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400">
              {presenceRate}%
            </span>
          </div>

          <div className="flex items-center justify-between text-zinc-600 dark:text-zinc-400">
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-blue-500" />
              <span>Privacy Certification</span>
            </span>
            <span className="font-semibold text-blue-600 dark:text-blue-400">
              GDPR & Privacy Included
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2.5 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-xs font-semibold text-zinc-600 dark:text-zinc-400 hover:text-black dark:hover:text-white transition"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleGenerate}
            disabled={generating}
            className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 active:scale-95 text-xs font-bold text-white shadow-lg shadow-blue-600/25 flex items-center gap-2 transition-all disabled:opacity-50"
          >
            {generating ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Generating Dossier...</span>
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                <span>Export Dossier (PDF)</span>
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
}
